import { useMemo, useState } from 'react';
import type { FilterColumn } from './filters.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- each column's own value type is only known inside that column's `FilterColumn<T, V>`; the map necessarily erases it to store every column's state in one object.
type AnyFilterColumn<T> = FilterColumn<T, any>;

export interface UseColumnFiltersResult<T> {
  /** `rows` after every active filter has been applied, combined with AND. */
  rows: T[];
  values: Record<string, unknown>;
  setValue(columnId: string, value: unknown): void;
  /** Resets every column back to its inactive (no filter) value. */
  clear(): void;
  activeCount: number;
  isActive(columnId: string): boolean;
}

/**
 * Per-column filter state for a table, client-side, combined with AND.
 * Each column supplies a `FilterColumn` (see `filters.ts`: text-contains,
 * exact-match select, or numeric/money range) and this hook only owns the
 * generic plumbing — current value per column, applying `matches`, and the
 * "how many filters are active" count the filter row shows next to its
 * clear control.
 */
export function useColumnFilters<T>(
  rows: T[],
  columns: Record<string, AnyFilterColumn<T>>,
): UseColumnFiltersResult<T> {
  const columnIds = Object.keys(columns).sort().join('|');
  const initialValues = useMemo(
    () => Object.fromEntries(Object.entries(columns).map(([id, col]) => [id, col.initial])),
    // Columns are a stable, page-defined configuration — recomputed only if
    // the *set* of column ids actually changes, not on every render where a
    // page re-creates the same config object literal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnIds],
  );
  const [values, setValues] = useState<Record<string, unknown>>(initialValues);

  function setValue(columnId: string, value: unknown) {
    setValues((prev) => ({ ...prev, [columnId]: value }));
  }

  function clear() {
    setValues(initialValues);
  }

  const filtered = useMemo(
    () =>
      rows.filter((row) =>
        Object.entries(columns).every(([id, col]) => col.matches(row, values[id] ?? col.initial)),
      ),
    [rows, columns, values],
  );

  const activeCount = Object.entries(columns).filter(([id, col]) =>
    col.isActive(values[id] ?? col.initial),
  ).length;

  function isActive(columnId: string): boolean {
    const col = columns[columnId];
    if (!col) return false;
    return col.isActive(values[columnId] ?? col.initial);
  }

  return { rows: filtered, values, setValue, clear, activeCount, isActive };
}
